// TipTap Mark for external links: [text](https://…) markdown. Deliberately
// minimal (no autolink/paste detection) — full control over serialization
// matters more here than convenience input rules, and the pre-TipTap editor
// never had those either (it just inserted raw `[text](url)` text).
import { safeExternalHref } from '@/lib/word-links';
import { Mark, mergeAttributes } from '@tiptap/core';

export interface ExternalLinkOptions {
  HTMLAttributes: Record<string, unknown>;
}

declare module '@tiptap/core' {
  interface Commands<ReturnType> {
    externalLink: {
      setExternalLink: (attrs: { href: string }) => ReturnType;
    };
  }
}

export const ExternalLink = Mark.create<ExternalLinkOptions>({
  name: 'link',

  addOptions() {
    return { HTMLAttributes: {} };
  },

  addAttributes() {
    return {
      href: {
        default: null,
        parseHTML: (el) => el.getAttribute('href'),
        renderHTML: (attrs) => ({ href: attrs.href }),
      },
    };
  },

  parseHTML() {
    return [{ tag: 'a[href]' }];
  },

  renderHTML({ HTMLAttributes }) {
    // Neutralize unsafe schemes (javascript:, data:, …) at render time: the raw
    // value stays in the mark's attrs so markdown serialization round-trips
    // byte-for-byte, but the live anchor never carries an executable href.
    const rawHref = typeof HTMLAttributes.href === 'string' ? HTMLAttributes.href : '';
    const safeHref = safeExternalHref(rawHref);
    const { href: _dropped, ...rest } = HTMLAttributes;
    return [
      'a',
      mergeAttributes(this.options.HTMLAttributes, rest, {
        ...(safeHref !== null ? { href: safeHref } : {}),
        target: '_blank',
        rel: 'noopener noreferrer',
        class: 'editor-external-link',
      }),
      0,
    ];
  },

  addCommands() {
    return {
      setExternalLink:
        (attrs) =>
        ({ commands }) =>
          commands.setMark(this.name, attrs),
    };
  },
});
